<template>
  <EntityForm :entity-id="serviceProviderId" v-model:entity="serviceProvider"
              v-model:editState="isEditingServiceProvider" form-name="Service Provider" api-url="/api/service-provider"
              redirect-url="/admin/service-provider" model-constructor="ServiceProvider" v-bind:show-title=true
              v-bind:file-upload-map="fileUploadMap">

    <!-- Wait until `serviceProvider` is defined before rendering the form -->
    <div v-if="serviceProvider">
      <!-- Service Provider Logo -->
      <div class="row mb-3">
        <div class="col-md-4">
          <div class="mb-3">
            <label class="form-label f-w-600">Logo</label>
            <div class="logo-container">
              <img v-if="serviceProvider.state.logoUrl" :src="`/api/service-provider/${serviceProviderId}/logo`"
                   alt="Service Provider Logo" class="img-fluid mb-2" style="max-height: 150px; max-width: 100%;"/>
              <div v-else class="mb-2 d-flex align-items-center justify-content-center bg-light"
                   style="height: 150px; width: 100%;">
                <i class="fa-regular fa-image fa-4x text-muted"></i>
              </div>
              <input v-if="isEditingServiceProvider" type="file" class="form-control" id="logo-upload"
                     @change="handleLogoUpload" accept="image/*"/>
            </div>
          </div>
        </div>
        <div class="col-md-8">
          <!-- Service Provider Name -->
          <div class="mb-3">
            <label class="form-label f-w-600" for="service-provider-name-input">Name</label>
            <input id="service-provider-name-input" v-model="serviceProvider.state.name" class="form-control"
                   type="text" placeholder="Service provider name" :disabled="!isEditingServiceProvider"
                   :readonly="!isEditingServiceProvider"/>
            <div id="service-provider-name-error" class="invalid-feedback d-none">Please enter a name.</div>
          </div>
        </div>
      </div>

      <!-- Address -->
      <div class="row">
        <div class="col">
          <div class="mb-3">
            <label class="form-label f-w-600" for="service-provider-address-input">Address</label>
            <input id="service-provider-address-input" v-model="serviceProvider.state.address" class="form-control"
                   type="text" placeholder="23 City Street" :disabled="!isEditingServiceProvider"
                   :readonly="!isEditingServiceProvider" ref="serviceProviderAddressInput"/>
            <div id="service-provider-address-error" class="invalid-feedback d-none">Please enter an address.</div>
          </div>
        </div>
      </div>

      <!-- Email and Phone Number -->
      <div class="row">
        <div class="col-sm-6">
          <div class="mb-3">
            <label class="form-label f-w-600" for="service-provider-email-input">Email</label>
            <input id="service-provider-email-input" v-model="serviceProvider.state.email" class="form-control"
                   type="email" placeholder="service.provider@email.com" :disabled="!isEditingServiceProvider"
                   :readonly="!isEditingServiceProvider"/>
            <div id="service-provider-email-error" class="invalid-feedback d-none">Please enter an email.</div>
          </div>
        </div>
        <div class="col-sm-6">
          <div class="mb-3">
            <label class="form-label f-w-600" for="service-provider-phone-number-input">Phone Number</label>
            <input id="service-provider-phone-number-input" v-model="serviceProvider.state.phoneNumber"
                   class="form-control" type="tel" placeholder="Service provider phone number"
                   :disabled="!isEditingServiceProvider" :readonly="!isEditingServiceProvider"/>
            <div id="service-provider-phone-number-error" class="invalid-feedback d-none">Please enter a valid phone
              number.
            </div>
          </div>
        </div>
      </div>

      <!-- Dispatch Enabled Checkbox -->
      <div class="row">
        <div class="col">
          <div class="form-check mb-3">
            <input id="service-provider-dispatcher-checkbox" v-model="serviceProvider.state.dispatchEnabled"
                   class="form-check-input" type="checkbox" :disabled="!isEditingServiceProvider"/>
            <label class="form-check-label" for="service-provider-dispatcher-checkbox">Dispatch Enabled</label>
          </div>
        </div>
      </div>

      <!-- Contact Person Details -->
      <div class="row">
        <div class="col-sm-6">
          <div class="mb-3">
            <label class="form-label f-w-600" for="service-provider-contact-name-input">Contact Person Name</label>
            <input id="service-provider-contact-name-input" v-model="serviceProvider.state.contactPersonName"
                   class="form-control" type="text" placeholder="Contact person name"
                   :disabled="!isEditingServiceProvider"
                   :readonly="!isEditingServiceProvider"/>
            <div id="service-provider-contact-name-error" class="invalid-feedback d-none">Please enter a contact
              person's name.
            </div>
          </div>
        </div>
        <div class="col-sm-6">
          <div class="mb-3">
            <label class="form-label f-w-600" for="service-provider-contact-phone-input">Contact Person Phone</label>
            <input id="service-provider-contact-phone-input" v-model="serviceProvider.state.contactPersonPhone"
                   class="form-control" type="tel" placeholder="Contact person phone number"
                   :disabled="!isEditingServiceProvider" :readonly="!isEditingServiceProvider"/>
            <div id="service-provider-contact-phone-error" class="invalid-feedback d-none">Please enter a valid
              contact's phone number.
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-6">
          <div class="mb-3">
            <label class="form-label f-w-600" for="service-provider-contact-email-input">Contact Person Email</label>
            <input id="service-provider-contact-email-input" v-model="serviceProvider.state.contactPersonEmail"
                   class="form-control" type="email" placeholder="Contact person email"
                   :disabled="!isEditingServiceProvider"
                   :readonly="!isEditingServiceProvider"/>
            <div id="service-provider-contact-email-error" class="invalid-feedback d-none">Please enter a valid
              contact's email.
            </div>
          </div>
        </div>
        <div class="col-12">
          <div class="mb-3">
            <label class="form-label f-w-600" for="service-provider-operating-hours-input">Operating Hours</label>
            <textarea id="service-provider-operating-hours-input" v-model="serviceProvider.state.operatingHours"
                      class="form-control" placeholder="Operating hours" :disabled="!isEditingServiceProvider"
                      :readonly="!isEditingServiceProvider"/>
          </div>
        </div>
      </div>

      <!-- Service Areas -->
      <div class="row">
        <div class="col-12">
          <div class="mb-3">
            <label class="form-label f-w-600" for="service-provider-service-areas-input">Service Areas</label>
            <div class="d-block">
              <input class=" form-control" name="tags3-1" placeholder="Search service areas"
                     ref="tagifyServiceAreasRef"/>
            </div>
            <small class="form-text text-muted">Select the service areas this provider operates in.</small>
          </div>
        </div>
      </div>

      <!-- Service Area Allowance -->
      <div class="row">
        <div class="col-12">
          <div class="mb-3">
            <label class="form-label f-w-600" for="service-provider-service-area-allowance-input">Service Area Allowance
              (km)</label>
            <input id="service-provider-service-area-allowance-input"
                   v-model="serviceProvider.state.serviceAreaAllowance"
                   class="form-control" type="number" min="0" max="10" step="0.1"
                   placeholder="Maximum distance outside service area (km)"
                   :disabled="!isEditingServiceProvider" :readonly="!isEditingServiceProvider"/>
            <div id="service-provider-service-area-allowance-error" class="invalid-feedback d-none">
              Please enter a valid allowance between 0 and 10 km.
            </div>
            <small class="form-text text-muted">Maximum distance (in km) that the service provider can operate outside
              of their selected service areas. Maximum value is 10km.</small>
          </div>
        </div>
      </div>
    </div>
  </EntityForm>

  <div v-if="!isNewServiceProvider">
    <Suspense>
      <FareConfigurationComponent :service-provider-id="serviceProviderId"/>
    </Suspense>

    <div class="col-xl-12 xl-100 box-col-6">
      <div class="card">
        <div class="card-header">
          <h5>
            Dispatchers
          </h5>
        </div>

        <div class="card-body social-list filter-cards-view">
          <div class="col-sm-3 d-md-block mb-4">
            <a class="btn btn-primary align-items-center" @click="addDispatchUser" type="button" data-bs-toggle="modal"
               data-bs-target="#dispatcher-modal">
              <i class="fa-regular fa-square-plus"></i>
              Add Dispatch User
            </a>
          </div>
          <div>
            <DataTable
                :ajax="{ url: `/api/datatable/sp-dispatch-users/${serviceProviderId}` }"
                ref="dispatcherDataTable" class="table-responsive display" :columns="dispatcherColumns"/>
          </div>
        </div>
      </div>
    </div>

    <div class="col-xl-12 xl-100 box-col-6 mt-4">
      <div class="card">
        <div class="card-header">
          <h5>
            Drivers
          </h5>
        </div>

        <div class="card-body social-list filter-cards-view">
          <div class="col-sm-3 d-md-block mb-4">
            <a class="btn btn-primary align-items-center" @click="addDriver" type="button" data-bs-toggle="modal"
               data-bs-target="#driver-modal">
              <i class="fa-regular fa-square-plus"></i>
              Add Driver
            </a>
          </div>
          <div>
            <DataTable
                :ajax="{ url: `/api/datatable/sp-all-drivers/${serviceProviderId}` }"
                ref="driverDataTable" class="table-responsive display" :columns="driverColumns"/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted, nextTick, watch, onUnmounted} from 'vue';
import 'jquery-ui';
import EntityForm from './EntityForm.vue';
import Swal from "../../extras/js/sweetalert/sweetalert2.min";
import DataTable from 'datatables.net-vue3';
import DataTablesCore from 'datatables.net-bs5';
import FareConfigurationComponent from './FareConfigurationComponent.vue';
import {booleanBadgeRender, driverStatus, userDetailsWithThumbnail, zonedDateTime} from '../utility/DatatableRenderer';
import Tagify from '../../extras/js/vendors/@yaireo/tagify/dist/tagify';
import {newEntityId} from '../globals.js';

DataTable.use(DataTablesCore);

const props = defineProps({
  serviceProviderId: {type: String, default: newEntityId}
});


const dispatcherDataTable = ref(null);
const driverDataTable = ref(null);

// Reactive variables for Service Provider
const isNewServiceProvider = ref(props.serviceProviderId === newEntityId);
const serviceProvider = ref({});
const isEditingServiceProvider = ref(false);
const fileUploadMap = ref({});
const tagifyServiceAreaList = ref([]);
const tagifyInstance = ref(null);
const tagifyServiceAreasRef = ref(null);

// Fetch available service areas
const fetchServiceAreas = async () => {
  try {
    const response = await fetch('/api/service-area-zones');
    if (response.ok) {
      const data = await response.json();
      tagifyServiceAreaList.value = data.map(area => ({
        id: area.id,
        value: area.name,
        name: area.name,
        description: area.description,
        zoneType: area.zoneType
      }));
    } else {
      console.error('Failed to fetch service areas:', response.status);
    }
  } catch (error) {
    console.error('Error fetching service areas:', error);
    Swal.fire({
      title: 'Error',
      text: 'Failed to load service areas',
      icon: 'error'
    });
  }
};

const initializeTagify = async () => {
  await nextTick();
  // If Tagify is already initialized, destroy it first
  if (tagifyInstance.value) {
    tagifyInstance.value.destroy();
    tagifyInstance.value = null;
  }

  // Initialize Tagify on the service areas input
  tagifyInstance.value = new Tagify(tagifyServiceAreasRef.value, {
    readOnly: true,
    delimiters: null,
    templates: {
      tag: function (tagData) {
        try {
          return `<tag title='${tagData.value}' contenteditable='false' spellcheck="false" class='tagify__tag ${tagData.class ? tagData.class : ""}' ${this.getAttributes(tagData)}>
                      <x title='remove tag' class='tagify__tag__removeBtn'></x>
                      <div>
                          <span class='tagify__tag-text'>${tagData.value}</span>
                      </div>
                  </tag>`;
        } catch (err) {
        }
      },
      dropdownItem: function (tagData) {
        try {
          return `<div ${this.getAttributes(tagData)} class='tagify__dropdown__item ${tagData.class ? tagData.class : ""}'>
                    <span>${tagData.value}</span>
                  </div>`;
        } catch (err) {
          console.error(err);
        }
      },
    },
    maxTags: 10,
    enforceWhitelist: true,
    whitelist: tagifyServiceAreaList.value,
    dropdown: {
      closeOnSelect: false,
      highlightFirst: true,
      enabled: 0,
      classname: "extra-properties", // custom class for the suggestions dropdown
    },
  });

  // Set initial value if serviceProvider has serviceAreas
  if (serviceProvider.value?.state?.serviceAreas?.length) {
    const initialTags = serviceProvider.value.state.serviceAreas.map(area => {
      const foundArea = tagifyServiceAreaList.value.find(a => a.id === area.id);
      return foundArea ? {
        value: foundArea.name.toString(),
        name: foundArea.name,
        id: foundArea.id,
        description: foundArea.description,
        zoneType: foundArea.zoneType
      } : null;
    }).filter(Boolean);

    if (initialTags.length > 0) {
      tagifyInstance.value.addTags(initialTags);
    }
  }

  tagifyInstance.value.setReadonly(true);
  tagifyInstance.value.on("remove", updateServiceAreas);
  tagifyInstance.value.on("add", updateServiceAreas);


};


// Update serviceProvider.state.serviceAreas when tags change
const updateServiceAreas = () => {
  if (!tagifyInstance.value || !serviceProvider.value?.state) return;
  try {
    const tags = tagifyInstance.value.value || [];
    serviceProvider.value.state.serviceAreas = tags.map(tag => {
      const area = tagifyServiceAreaList.value.find(a => a.value.toString() === tag.value.toString());
      return area ? {
        id: area.id,
        name: area.name,
        description: area.description,
        zoneType: area.zoneType
      } : null;
    }).filter(Boolean);

  } catch (error) {
    console.error('Error updating service areas:', error);
  }
};

// Watch for changes in isEditingServiceProvider
watch(() => isEditingServiceProvider.value, (newVal) => {
  if (tagifyInstance.value) {
    tagifyInstance.value.setReadonly(!newVal);
  }
  try {
    if (!newVal && serviceProviderAddressInput.value) {
      $(serviceProviderAddressInput.value).autocomplete('close');
    }
  } catch (e) {
    // ignore
  }
});

// Initialize everything when component is mounted
onMounted(async () => {
  await fetchServiceAreas();
  // Wait a bit more for the DOM to be ready
  setTimeout(() => {
    if (tagifyServiceAreaList.value.length > 0) {
      initializeTagify();
    }
  }, 100);
});

// Cleanup when component is unmounted
onUnmounted(() => {
  if (tagifyInstance.value) {
    tagifyInstance.value.destroy();
    tagifyInstance.value = null;
  }
  try {
    if (serviceProviderAddressInput.value) {
      $(serviceProviderAddressInput.value).autocomplete('destroy');
    }
  } catch (e) {
    // ignore
  }
});

// Handle logo upload
const handleLogoUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Check file type
  if (!file.type.match('image.*')) {
    Swal.fire({
      title: 'Error',
      text: 'Please select an image file',
      icon: 'error'
    });
    return;
  }

  // Check file size (max 5MB)
  if (file.size > 5 * 1024 * 1024) {
    Swal.fire({
      title: 'Error',
      text: 'File size should not exceed 5MB',
      icon: 'error'
    });
    return;
  }

  fileUploadMap.value["logo"] = file;
};

// Add a new Dispatch User
const addDispatchUser = () => {
  //open in new tab
  window.open(`/service-provider/${props.serviceProviderId}/dispatch-user/new`, '_blank');
};

// Add a new Driver
const addDriver = () => {
  //open in new tab
  window.open(`/service-provider/${props.serviceProviderId}/driver/new`, '_blank');
};

// Datatable columns
const dispatcherColumns = [
  {data: 'fullName', title: 'Name'},
  {data: 'email', title: 'Email'},
  {data: 'isAdminUser', title: 'Admin User', render: booleanBadgeRender},
  {
    title: 'Actions', orderable: false, searchable: false, render: (data, type, row) => {
      return `<a class="btn btn-outline-primary" data-id="${row.id}"
                href="/service-provider/${props.serviceProviderId}/dispatch-user/${row.id}">
                Details <i class="fa-solid fa-arrow-up-right-from-square"></i> </a>`;
    }
  }
];

// Driver datatable columns
const driverColumns = [
  {
    title: "Driver", data: "name", render: (data, type, row) => {
      return userDetailsWithThumbnail(data, row, `/service-provider/${props.serviceProviderId}/driver/${row.id}`);
    }
  },
  {data: "email", title: "Email"},
  {data: "phoneNumber", title: 'Phone Number'},
  {data: "driverStatus", title: 'Status', render: driverStatus},
  {data: "joinedOn", title: 'Joined At', render: zonedDateTime},
  {
    title: 'Actions', orderable: false, searchable: false, render: (data, type, row) => {
      return `<a class="btn btn-outline-primary" data-id="${row.id}"
              href="/service-provider/${props.serviceProviderId}/driver/${row.id}">
              Details <i class="fa-solid fa-arrow-up-right-from-square"></i> </a>`;
    }
  }
];
</script>
